/* AuthScreen — onboarding / registration */
import { Button, Input } from '@koridor/design-system';
import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '@koridor/api-client';
import { signInWithApple } from '../lib/apple-signin';
import { canUseNativeAppleSignIn, nativeAppleSignIn } from '../lib/native-apple-signin';
import { useKeyboardInset } from '../lib/use-keyboard-inset';

function BrandMark({ size = 64 }: { size?: number }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: size * 0.3, background: 'var(--clay-500)', flexShrink: 0,
      display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gridTemplateRows: 'repeat(3,1fr)',
      gap: size * 0.06, padding: size * 0.16, boxSizing: 'border-box', boxShadow: 'var(--shadow-accent)',
    }}>
      {Array.from({ length: 9 }).map((_, i) => <span key={i} style={{ background: i === 4 ? '#fff' : 'rgba(255,255,255,.28)', borderRadius: i === 4 ? '50%' : 3 }} />)}
    </span>
  );
}

function AppleGlyph() {
  return (
    <svg width="15" height="18" viewBox="0 0 14 17" fill="#fff" aria-hidden="true" style={{ marginTop: -2 }}>
      <path d="M11.5 9.02c-.02-1.7 1.39-2.52 1.45-2.56-.79-1.16-2.02-1.31-2.46-1.33-1.05-.11-2.04.61-2.57.61-.53 0-1.35-.6-2.22-.58-1.14.02-2.19.66-2.78 1.68-1.18 2.05-.3 5.09.85 6.75.56.81 1.23 1.72 2.11 1.69.85-.03 1.17-.55 2.19-.55 1.02 0 1.31.55 2.2.53.91-.02 1.49-.83 2.05-1.64.64-.94.91-1.85.92-1.9-.02-.01-1.77-.68-1.79-2.7zM9.8 3.6c.47-.57.79-1.36.7-2.15-.68.03-1.5.45-1.98 1.02-.43.5-.81 1.3-.71 2.07.76.06 1.53-.39 1.99-.94z"/>
    </svg>
  );
}

export function AuthScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { login, register, appleLogin, enterGuest } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [formLoading, setFormLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const keyboardInset = useKeyboardInset();
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;

  function afterAuth() {
    if (redirectTo?.startsWith('/') && !redirectTo.startsWith('//')) {
      navigate(redirectTo);
      return;
    }
    navigate('/');
  }

  function playOffline() {
    enterGuest();
    afterAuth();
  }

  function switchMode(m: 'signup' | 'login') { setMode(m); setErrors({}); }

  async function submit() {
    const e: Record<string, string> = {};
    if (mode === 'signup' && !name.trim()) e.name = 'Введите никнейм';
    if (!email.trim()) e.email = 'Введите почту';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Неверный формат почты';
    if (!pass) e.pass = 'Введите пароль';
    else if (pass.length < 8) e.pass = 'Минимум 8 символов';
    setErrors(e);
    if (Object.keys(e).length) return;

    setFormLoading(true);
    try {
      if (mode === 'signup') await register(name.trim(), email.trim(), pass);
      else await login(email.trim(), pass);
      afterAuth();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.field) setErrors({ [err.field]: err.message });
        else setErrors({ pass: err.message });
      } else {
        setErrors({ pass: err instanceof Error ? err.message : 'Нет связи с сервером' });
      }
    } finally {
      setFormLoading(false);
    }
  }

  async function appleSignIn() {
    setAppleLoading(true);
    setErrors({});
    try {
      const { identityToken, nickname } = (await canUseNativeAppleSignIn())
        ? await nativeAppleSignIn()
        : await signInWithApple();
      await appleLogin(identityToken, nickname ?? (name.trim() || undefined));
      afterAuth();
    } catch (err) {
      if (err instanceof ApiError) setErrors({ apple: err.message });
      else if (err instanceof Error && err.message !== 'popup_closed_by_user') {
        setErrors({ apple: err.message || 'Не удалось войти через Apple' });
      }
    } finally {
      setAppleLoading(false);
    }
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      width: '100%',
      height: '100%',
      maxHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      background: 'var(--bg-app)',
      overflow: 'hidden',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    }}>
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: keyboardInset > 0 ? keyboardInset : 0,
      }}>
        {keyboardInset === 0 && (
          <div style={{
            padding: '18px var(--gutter) 8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            textAlign: 'center',
            flexShrink: 0,
          }}>
            <BrandMark size={56} />
            <div>
              <div style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 800,
                fontSize: 32,
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}>
                Коридор
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, maxWidth: 260, lineHeight: 1.35 }}>
                Доведите пешку до края доски раньше соперника.
              </div>
            </div>
          </div>
        )}

        <div style={{
          marginTop: 'auto',
          padding: '8px var(--gutter) 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          flexShrink: 0,
        }}>
          <button type="button" onClick={appleSignIn} disabled={appleLoading || formLoading} style={{
            height: 'var(--control-h)', width: '100%', border: 'none', borderRadius: 'var(--radius-md)',
            background: '#000', color: '#fff', cursor: appleLoading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            fontFamily: 'var(--font-body)', fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)',
            opacity: appleLoading ? 0.7 : 1,
            flexShrink: 0,
          }}>
            <AppleGlyph /> {appleLoading ? 'Подключение к Apple…' : 'Войти с Apple'}
          </button>
          {errors.apple && (
            <div style={{ fontSize: 13, color: 'var(--danger)', textAlign: 'center' }}>{errors.apple}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-muted)', fontSize: 12 }}>
            <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} /><span>или почта</span><span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
          </div>
          {mode === 'signup' && (
            <Input label="Никнейм" placeholder="Как вас зовут?" value={name} error={errors.name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }} />
          )}
          <Input label="Почта" type="email" placeholder="you@mail.ru" value={email} error={errors.email}
            onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }} />
          <Input label="Пароль" type="password" placeholder="••••••••" value={pass} error={errors.pass}
            hint={mode === 'signup' && !errors.pass ? 'Минимум 8 символов' : undefined}
            onChange={(e) => { setPass(e.target.value); setErrors((p) => ({ ...p, pass: undefined })); }} />
          {errors.pass && (
            <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: -6, textAlign: 'center' }}>{errors.pass}</div>
          )}
          <Button variant="primary" size="lg" fullWidth loading={formLoading} onClick={submit}>
            {mode === 'signup' ? 'Создать профиль' : 'Войти'}
          </Button>
          <Button
            variant={offline ? 'primary' : 'secondary'}
            size="lg"
            fullWidth
            disabled={formLoading || appleLoading}
            onClick={playOffline}
          >
            Играть оффлайн
          </Button>
          {offline && (
            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.35 }}>
              Без сети доступны игра с ИИ и локальная партия.
            </div>
          )}
          {mode === 'signup' ? (
            <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)' }}>
              Уже играли? <span onClick={() => switchMode('login')} style={{ color: 'var(--text-accent)', fontWeight: 600, cursor: 'pointer' }}>Войти</span>
            </div>
          ) : (
            <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)' }}>
              Нет аккаунта? <span onClick={() => switchMode('signup')} style={{ color: 'var(--text-accent)', fontWeight: 600, cursor: 'pointer' }}>Создать</span>
            </div>
          )}
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            Регистрируясь, вы принимаете{' '}
            <Link to="/legal/terms">условия</Link> и{' '}
            <Link to="/legal/privacy">политику конфиденциальности</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}
